/**
 * Custom hook for Reports state management
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useView } from '../../contexts/ViewContext';
import {
  reportingAPI,
  type AnalyseCategory,
  type AnalyseDashboard,
  type DashboardIndicator,
  type Dataset,
  type ScheduledReportCreatePayload,
} from '../../lib/api/reporting';
import { toast } from 'sonner';

export interface NewReportState {
  name: string;
  description: string;
  category: number;
  dataset: number;
  view_type: string;
  shared_with: 'private' | 'company' | 'company_and_suppliers';
  download_lock: boolean;
}

export interface NewCategoryState {
  name: string;
  hint: string;
}

export interface NewIndicatorState {
  name: string;
  chart_type: DashboardIndicator['chart_type'];
  hint: string;
  config: Record<string, unknown>;
}

export type ExportFormat = 'csv' | 'xlsx' | 'pdf' | 'png';

/**
 * Reports v2 state machine. Owns dialog open/close flags + handlers
 * that wrap the reporting API for edit / duplicate / share / schedule
 * / export / filter / edit-indicator workflows.
 */
export function useReports() {
  const { getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get('view') || 'list';
  const selectedReportId = searchParams.get('report');

  // Data states
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [categories, setCategories] = useState<AnalyseCategory[]>([]);
  const [reports, setReports] = useState<AnalyseDashboard[]>([]);
  const [indicators, setIndicators] = useState<DashboardIndicator[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [selectedReport, setSelectedReport] = useState<AnalyseDashboard | null>(null);

  // UI states
  const [loading, setLoading] = useState(true);
  const [loadingIndicators, setLoadingIndicators] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Dialog states (v1)
  const [isCreateReportOpen, setIsCreateReportOpen] = useState(false);
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false);
  const [isAddIndicatorOpen, setIsAddIndicatorOpen] = useState(false);
  const [isDeleteReportOpen, setIsDeleteReportOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<AnalyseDashboard | null>(null);

  // Dialog states (v2)
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [reportToEdit, setReportToEdit] = useState<AnalyseDashboard | null>(null);
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
  const [reportToDuplicate, setReportToDuplicate] = useState<AnalyseDashboard | null>(null);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [reportToShare, setReportToShare] = useState<AnalyseDashboard | null>(null);
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [reportToPublish, setReportToPublish] = useState<AnalyseDashboard | null>(null);
  const [isRevisionsOpen, setIsRevisionsOpen] = useState(false);
  const [reportForRevisions, setReportForRevisions] = useState<AnalyseDashboard | null>(null);
  const [isDownloadLogsOpen, setIsDownloadLogsOpen] = useState(false);
  const [isFiscalCalendarOpen, setIsFiscalCalendarOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isEditIndicatorOpen, setIsEditIndicatorOpen] = useState(false);
  const [indicatorToEdit, setIndicatorToEdit] = useState<DashboardIndicator | null>(null);

  // Form states
  const [newReport, setNewReport] = useState<NewReportState>({
    name: '',
    description: '',
    category: 0,
    dataset: 0,
    view_type: 'grid',
    shared_with: 'company',
    download_lock: false,
  });
  const [newCategory, setNewCategory] = useState<NewCategoryState>({ name: '', hint: '' });
  const [newIndicator, setNewIndicator] = useState<NewIndicatorState>({
    name: '',
    chart_type: 'bar',
    hint: '',
    config: {},
  });
  const [saving, setSaving] = useState(false);

  /**
   * Fetch every dashboard the user can see without a silent cap. Walks the
   * paginated response until the server reports no `next` cursor. Caps at a
   * safety ceiling (10 pages of 100) so a broken backend can't hang the UI.
   */
  const fetchAllDashboards = useCallback(
    async (baseParams: Record<string, string | number>) => {
      const pageSize = 100;
      const maxPages = 10;
      const acc: AnalyseDashboard[] = [];
      for (let page = 1; page <= maxPages; page += 1) {
        const res = await reportingAPI.getDashboards({
          ...baseParams,
          page,
          page_size: pageSize,
        });
        const chunk = res.data.results || [];
        acc.push(...chunk);
        if (!res.data.next || chunk.length < pageSize) break;
      }
      return acc;
    },
    [],
  );

  // Fetch indicators for a report — declared before fetchData uses it
  const fetchIndicators = useCallback(async (reportId: number) => {
    setLoadingIndicators(true);
    try {
      const response = await reportingAPI.getIndicators({
        dashboard: reportId,
      });
      setIndicators(response.data.results || []);
    } catch (error) {
      console.error('Failed to fetch indicators:', error);
      toast.error('Failed to load report indicators');
    } finally {
      setLoadingIndicators(false);
    }
  }, []);

  // Fetch initial data
  useEffect(() => {
    if (viewContextLoading) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const rawViewParams = getViewQueryParams();
        const viewParams: Record<string, number> = {};
        Object.entries(rawViewParams).forEach(([k, v]) => { if (v !== undefined) viewParams[k] = v; });
        const [datasetsRes, categoriesRes, allDashboards] = await Promise.all([
          reportingAPI.getDatasets({ ...viewParams, enabled: true }),
          reportingAPI.getCategories(viewParams),
          fetchAllDashboards(viewParams),
        ]);
        setDatasets(datasetsRes.data.results || []);
        setCategories(categoriesRes.data.results || []);
        setReports(allDashboards);

        // If report ID in URL, load that report
        if (selectedReportId) {
          const report = allDashboards.find(
            (d) => d.id === parseInt(selectedReportId)
          );
          if (report) {
            setSelectedReport(report);
            void fetchIndicators(report.id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load reports data');
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewContextLoading, selectedReportId, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  // Refresh just the dashboard list (used after edit/duplicate)
  const refreshReports = useCallback(async () => {
    try {
      const rawViewParams = getViewQueryParams();
      const viewParams: Record<string, string | number> = {};
      Object.entries(rawViewParams).forEach(([k, v]) => { if (v !== undefined) viewParams[k] = v; });
      const dashboards = await fetchAllDashboards(viewParams);
      setReports(dashboards);
      return dashboards;
    } catch (err: unknown) {
      console.error('Failed to refresh reports:', err);
      return [];
    }
  }, [getViewQueryParams, fetchAllDashboards]);

  // Handle view report
  const handleViewReport = (report: AnalyseDashboard) => {
    setSelectedReport(report);
    setSearchParams({ view: 'detail', report: report.id.toString() });
    void fetchIndicators(report.id);
  };

  // Handle back to list
  const handleBackToList = () => {
    setSelectedReport(null);
    setIndicators([]);
    setSearchParams({ view: 'list' });
  };

  // Handle refresh
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const rawViewParams = getViewQueryParams();
      const viewParams: Record<string, number> = {};
      Object.entries(rawViewParams).forEach(([k, v]) => { if (v !== undefined) viewParams[k] = v; });
      const [datasetsRes, categoriesRes, allDashboards] = await Promise.all([
        reportingAPI.getDatasets({ ...viewParams, enabled: true }),
        reportingAPI.getCategories(viewParams),
        fetchAllDashboards(viewParams),
      ]);
      setDatasets(datasetsRes.data.results || []);
      setCategories(categoriesRes.data.results || []);
      setReports(allDashboards);

      if (selectedReport) {
        void fetchIndicators(selectedReport.id);
      }
      toast.success('Data refreshed');
    } catch (error) {
      console.error('Failed to refresh:', error);
      toast.error('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  // Create report
  const handleCreateReport = async () => {
    if (!newReport.name.trim()) return;

    setSaving(true);
    try {
      const response = await reportingAPI.createDashboard({
        name: newReport.name.trim(),
        description: newReport.description.trim() || undefined,
        category: newReport.category || undefined,
        dataset: newReport.dataset || undefined,
        is_enabled: true,
        shared_with: newReport.shared_with,
        download_lock: newReport.download_lock,
      });
      setReports([...reports, response.data]);
      setNewReport({
        name: '',
        description: '',
        category: 0,
        dataset: 0,
        view_type: 'grid',
        shared_with: 'company',
        download_lock: false,
      });
      setIsCreateReportOpen(false);
      toast.success('Report created successfully');
      handleViewReport(response.data);
    } catch (error) {
      console.error('Failed to create report:', error);
      toast.error('Failed to create report');
    } finally {
      setSaving(false);
    }
  };

  // Create category
  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) return;

    setSaving(true);
    try {
      const response = await reportingAPI.createCategory({
        name: newCategory.name.trim(),
        hint: newCategory.hint.trim() || undefined,
      });
      setCategories([...categories, response.data]);
      setNewCategory({ name: '', hint: '' });
      setIsCreateCategoryOpen(false);
      toast.success('Category created');
    } catch (error) {
      console.error('Failed to create category:', error);
      toast.error('Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  // Create indicator. Returns the created indicator (or null on failure)
  // so callers can chain into the visual builder if requested.
  const handleCreateIndicator = async (): Promise<DashboardIndicator | null> => {
    if (!newIndicator.name.trim() || !selectedReport) return null;

    setSaving(true);
    try {
      const response = await reportingAPI.createIndicator({
        name: newIndicator.name.trim(),
        chart_type: newIndicator.chart_type,
        hint: newIndicator.hint.trim() || undefined,
        dashboard: selectedReport.id,
        config: newIndicator.config,
      });
      setIndicators([...indicators, response.data]);
      setNewIndicator({ name: '', chart_type: 'bar', hint: '', config: {} });
      setIsAddIndicatorOpen(false);
      toast.success('Indicator added');
      return response.data;
    } catch (error) {
      console.error('Failed to create indicator:', error);
      toast.error('Failed to add indicator');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Delete report
  const handleDeleteReport = async () => {
    if (!reportToDelete) return;

    try {
      await reportingAPI.deleteDashboard(reportToDelete.id);
      setReports(reports.filter(r => r.id !== reportToDelete.id));
      setReportToDelete(null);
      setIsDeleteReportOpen(false);
      if (selectedReport?.id === reportToDelete.id) {
        handleBackToList();
      }
      toast.success('Report deleted');
    } catch (error) {
      console.error('Failed to delete report:', error);
      toast.error('Failed to delete report');
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // Reports v2 — open helpers + handlers
  // ─────────────────────────────────────────────────────────────────

  const openEditReport = (report: AnalyseDashboard) => {
    setReportToEdit(report);
    setIsEditOpen(true);
  };

  const openDuplicateReport = (report: AnalyseDashboard) => {
    setReportToDuplicate(report);
    setIsDuplicateOpen(true);
  };

  const openShareReport = (report: AnalyseDashboard) => {
    setReportToShare(report);
    setIsShareOpen(true);
  };

  const openPublishReport = (report: AnalyseDashboard) => {
    setReportToPublish(report);
    setIsPublishOpen(true);
  };

  const openRevisionHistory = (report: AnalyseDashboard) => {
    setReportForRevisions(report);
    setIsRevisionsOpen(true);
  };

  const openDownloadLogs = () => setIsDownloadLogsOpen(true);
  const openFiscalCalendar = () => setIsFiscalCalendarOpen(true);

  const openScheduleReport = () => setIsScheduleOpen(true);
  const openExportReport = () => setIsExportOpen(true);
  const openFilterDialog = () => setIsFilterOpen(true);

  const openEditIndicator = (indicator: DashboardIndicator) => {
    setIndicatorToEdit(indicator);
    setIsEditIndicatorOpen(true);
  };

  /** PATCH the dashboard, refresh the list, sync `selectedReport`. */
  const handleEditReport = async (payload: Partial<AnalyseDashboard>) => {
    if (!reportToEdit) return;
    setSaving(true);
    try {
      const response = await reportingAPI.updateDashboard(reportToEdit.id, payload);
      setReports((prev) => prev.map((r) => (r.id === reportToEdit.id ? response.data : r)));
      if (selectedReport?.id === reportToEdit.id) {
        setSelectedReport(response.data);
      }
      setIsEditOpen(false);
      setReportToEdit(null);
      toast.success('Report updated');
    } catch (err: unknown) {
      console.error('Failed to update report:', err);
      toast.error('Failed to update report');
    } finally {
      setSaving(false);
    }
  };

  /** Clone the dashboard server-side, then navigate into the new copy. */
  const handleDuplicateReport = async (newName: string) => {
    if (!reportToDuplicate) return;
    setSaving(true);
    try {
      const response = await reportingAPI.duplicateDashboard(reportToDuplicate.id, newName);
      await refreshReports();
      setIsDuplicateOpen(false);
      setReportToDuplicate(null);
      toast.success('Report duplicated');
      handleViewReport(response.data);
    } catch (err: unknown) {
      console.error('Failed to duplicate report:', err);
      toast.error('Failed to duplicate report');
    } finally {
      setSaving(false);
    }
  };

  /** Save the coarse `shared_with` setting on a dashboard. */
  const handleShareReport = async (sharedWith: AnalyseDashboard['shared_with']) => {
    if (!reportToShare) return;
    setSaving(true);
    try {
      const response = await reportingAPI.shareDashboard(reportToShare.id, sharedWith);
      setReports((prev) => prev.map((r) => (r.id === reportToShare.id ? response.data : r)));
      if (selectedReport?.id === reportToShare.id) {
        setSelectedReport(response.data);
      }
      toast.success('Sharing updated');
    } catch (err: unknown) {
      console.error('Failed to update sharing:', err);
      toast.error('Failed to update sharing');
    } finally {
      setSaving(false);
    }
  };

  /** Persist a scheduled email delivery for the active report. */
  const handleScheduleReport = async (payload: ScheduledReportCreatePayload) => {
    setSaving(true);
    try {
      await reportingAPI.createSchedule(payload);
      setIsScheduleOpen(false);
      toast.success('Schedule saved');
    } catch (err: unknown) {
      console.error('Failed to save schedule:', err);
      toast.error('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Trigger a browser download via the export endpoint. The resolver applies
   * the indicator's stored filters automatically, so no per-download toggle
   * is needed.
   */
  const handleExportReport = async (
    format: ExportFormat,
    _applyFilters: boolean,
    indicatorIds?: number[],
  ) => {
    if (!selectedReport) return;
    try {
      await reportingAPI.downloadDashboardExport(selectedReport.id, format, indicatorIds);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Export failed';
      toast.error(message);
    }
  };

  /** After filter add/remove — refresh chart data for the active report. */
  const handleAfterFilterChange = () => {
    if (selectedReport) {
      void fetchIndicators(selectedReport.id);
    }
  };

  /** PATCH the indicator name + chart_type. */
  const handleEditIndicator = async (payload: Partial<DashboardIndicator>) => {
    if (!indicatorToEdit) return;
    setSaving(true);
    try {
      const response = await reportingAPI.updateIndicator(indicatorToEdit.id, payload);
      setIndicators((prev) => prev.map((i) => (i.id === indicatorToEdit.id ? response.data : i)));
      setIsEditIndicatorOpen(false);
      setIndicatorToEdit(null);
      toast.success('Chart updated');
    } catch (err: unknown) {
      console.error('Failed to update indicator:', err);
      toast.error('Failed to update chart');
    } finally {
      setSaving(false);
    }
  };

  /** Delete an indicator (with a native confirm to avoid accidental taps). */
  const handleDeleteIndicator = async (indicator: DashboardIndicator) => {
    if (!window.confirm(`Delete chart "${indicator.name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await reportingAPI.deleteIndicator(indicator.id);
      setIndicators((prev) => prev.filter((i) => i.id !== indicator.id));
      toast.success('Chart deleted');
    } catch (err: unknown) {
      console.error('Failed to delete indicator:', err);
      const message = err instanceof Error ? err.message : 'Failed to delete chart';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  /** Navigate to the visual report builder for a given indicator. */
  const handleOpenIndicatorBuilder = (indicator: DashboardIndicator) => {
    const params = new URLSearchParams();
    params.set('view', 'builder');
    params.set('indicator', indicator.id.toString());
    setSearchParams(params);
    setIsEditIndicatorOpen(false);
  };

  // Filter reports by search and dataset
  const filteredReports = useMemo(() => {
    let filtered = reports;

    if (searchValue) {
      const search = searchValue.toLowerCase();
      filtered = filtered.filter(
        r => r.name.toLowerCase().includes(search) ||
          r.description?.toLowerCase().includes(search)
      );
    }

    if (selectedDataset) {
      filtered = filtered.filter(r => r.dataset === selectedDataset.id);
    }

    return filtered;
  }, [reports, searchValue, selectedDataset]);

  // Group reports by category
  const reportsByCategory = useMemo(() => {
    const grouped: Record<string, AnalyseDashboard[]> = {};
    filteredReports.forEach((report) => {
      const categoryName = categories.find((c) => c.id === report.category)?.name || 'Uncategorized';
      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(report);
    });
    return grouped;
  }, [filteredReports, categories]);

  return {
    // Data
    datasets,
    categories,
    reports,
    indicators,
    selectedDataset,
    selectedReport,
    filteredReports,
    reportsByCategory,

    // UI states
    viewMode,
    loading,
    loadingIndicators,
    searchValue,
    saving,

    // Dialog states (v1)
    isCreateReportOpen,
    isCreateCategoryOpen,
    isAddIndicatorOpen,
    isDeleteReportOpen,
    reportToDelete,

    // Dialog states (v2)
    isEditOpen,
    reportToEdit,
    isDuplicateOpen,
    reportToDuplicate,
    isShareOpen,
    reportToShare,
    isPublishOpen,
    reportToPublish,
    isRevisionsOpen,
    reportForRevisions,
    isDownloadLogsOpen,
    isFiscalCalendarOpen,
    isScheduleOpen,
    isExportOpen,
    isFilterOpen,
    isEditIndicatorOpen,
    indicatorToEdit,

    // Form states
    newReport,
    newCategory,
    newIndicator,

    // Setters (v1)
    setSelectedDataset,
    setSearchValue,
    setIsCreateReportOpen,
    setIsCreateCategoryOpen,
    setIsAddIndicatorOpen,
    setIsDeleteReportOpen,
    setReportToDelete,
    setNewReport,
    setNewCategory,
    setNewIndicator,

    // Setters (v2)
    setIsEditOpen,
    setIsDuplicateOpen,
    setIsShareOpen,
    setIsPublishOpen,
    setIsRevisionsOpen,
    setIsDownloadLogsOpen,
    setIsFiscalCalendarOpen,
    setIsScheduleOpen,
    setIsExportOpen,
    setIsFilterOpen,
    setIsEditIndicatorOpen,

    // Open helpers
    openEditReport,
    openDuplicateReport,
    openShareReport,
    openPublishReport,
    openRevisionHistory,
    openDownloadLogs,
    openFiscalCalendar,
    openScheduleReport,
    openExportReport,
    openFilterDialog,
    openEditIndicator,

    // Actions
    handleViewReport,
    handleBackToList,
    handleRefresh,
    handleCreateReport,
    handleCreateCategory,
    handleCreateIndicator,
    handleDeleteReport,
    handleEditReport,
    handleDuplicateReport,
    handleShareReport,
    handleScheduleReport,
    handleExportReport,
    handleAfterFilterChange,
    handleEditIndicator,
    handleDeleteIndicator,
    handleOpenIndicatorBuilder,
  };
}
